import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import useDebounce from "../../../hooks/useDebounce";
import pushNotificationService from "../../../service/pushNotificationService";
import organizationService from "../../../service/organizationservice";
import errorHandler from "../../../utils/errorHandler";
import { Controller, useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CREATE_NOTIFICATION_VALIDATION } from "../../../validations/notification";
import {
  Button,
  Checkbox,
  Radio,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Close, Image, UploadFile, CalendarToday, AccessTime, FlashOn } from "@mui/icons-material";
import BTNLoader from "../../../lib/components/BTNLoader";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { toast } from "react-toastify";
import moment from "moment";
import PreviewNotification from "./notification-previewer";
import { EditorState, convertToRaw } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import draftToHtml from "draftjs-to-html";

const defaultValues = {
  title: "",
  date: new Date(),
  time: new Date(),
  scheduleType: "IMMEDIATE", // "IMMEDIATE" or "SCHEDULED"
  message: "",
  text: "",
  deliveryTypes: ["PUSH"],
  recipients: ["ORGANIZATION", "EMPLOYEE"],
  subscriptionPlans: ["ALL"],
  platforms: ["ALL"],
  organizationIds: ["ALL"],
  image: null,
};

const PushIcon = () => (
  <svg width="15" height="22" viewBox="0 0 15 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2 22C1.45 22 0.979167 21.8042 0.5875 21.4125C0.195833 21.0208 0 20.55 0 20V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H12C12.55 0 13.0208 0.195833 13.4125 0.5875C13.8042 0.979167 14 1.45 14 2V5.1C14.3 5.21667 14.5417 5.4 14.725 5.65C14.9083 5.9 15 6.18333 15 6.5V8.5C15 8.81667 14.9083 9.1 14.725 9.35C14.5417 9.6 14.3 9.78333 14 9.9V20C14 20.55 13.8042 21.0208 13.4125 21.4125C13.0208 21.8042 12.55 22 12 22H2ZM2 20H12V2H2V20ZM2 20V2V20ZM7 5C7.28333 5 7.52083 4.90417 7.7125 4.7125C7.90417 4.52083 8 4.28333 8 4C8 3.71667 7.90417 3.47917 7.7125 3.2875C7.52083 3.09583 7.28333 3 7 3C6.71667 3 6.47917 3.09583 6.2875 3.2875C6.09583 3.47917 6 3.71667 6 4C6 4.28333 6.09583 4.52083 6.2875 4.7125C6.47917 4.90417 6.71667 5 7 5Z"
      fill="#00347E"
    />
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM10 9L2 4V14H18V4L10 9ZM10 7L18 2H2L10 7ZM2 4V2V4V14V4Z"
      fill="#00347E"
    />
  </svg>
);

const SmsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 9C6.28333 9 6.52083 8.90417 6.7125 8.7125C6.90417 8.52083 7 8.28333 7 8C7 7.71667 6.90417 7.47917 6.7125 7.2875C6.52083 7.09583 6.28333 7 6 7C5.71667 7 5.47917 7.09583 5.2875 7.2875C5.09583 7.47917 5 7.71667 5 8C5 8.28333 5.09583 8.52083 5.2875 8.7125C5.47917 8.90417 5.71667 9 6 9ZM10 9C10.2833 9 10.5208 8.90417 10.7125 8.7125C10.9042 8.52083 11 8.28333 11 8C11 7.71667 10.9042 7.47917 10.7125 7.2875C10.5208 7.09583 10.2833 7 10 7C9.71667 7 9.47917 7.09583 9.2875 7.2875C9.09583 7.47917 9 7.71667 9 8C9 8.28333 9.09583 8.52083 9.2875 8.7125C9.47917 8.90417 9.71667 9 10 9ZM14 9C14.2833 9 14.5208 8.90417 14.7125 8.7125C14.9042 8.52083 15 8.28333 15 8C15 7.71667 14.9042 7.47917 14.7125 7.2875C14.5208 7.09583 14.2833 7 14 7C13.7167 7 13.4792 7.09583 13.2875 7.2875C13.0958 7.47917 13 7.71667 13 8C13 8.28333 13.0958 8.52083 13.2875 8.7125C13.4792 8.90417 13.7167 9 14 9ZM0 20V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H4L0 20ZM3.15 14H18V2H2V15.125L3.15 14ZM2 14V2V14Z"
      fill="#00347E"
    />
  </svg>
);

const InAppIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 22C13.1 22 14 21.1 14 20H10C10 21.1 10.9 22 12 22ZM18 16V11C18 7.93 16.37 5.36 13.5 4.68V4C13.5 3.17 12.83 2.5 12 2.5C11.17 2.5 10.5 3.17 10.5 4V4.68C7.64 5.36 6 7.92 6 11V16L4 18V19H20V18L18 16Z"
      fill="#00347E"
    />
  </svg>
);

const CloudUploadFigmaIcon = () => (
  <svg width="33" height="24" viewBox="0 0 33 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.25 24C5.975 24 4.03125 23.2125 2.41875 21.6375C0.80625 20.0625 0 18.1375 0 15.8625C0 13.9125 0.5875 12.175 1.7625 10.65C2.9375 9.125 4.475 8.15 6.375 7.725C7 5.425 8.25 3.5625 10.125 2.1375C12 0.7125 14.125 0 16.5 0C19.425 0 21.9062 1.01875 23.9438 3.05625C25.9813 5.09375 27 7.575 27 10.5C28.725 10.7 30.1562 11.4437 31.2938 12.7312C32.4313 14.0188 33 15.525 33 17.25C33 19.125 32.3438 20.7188 31.0312 22.0312C29.7188 23.3438 28.125 24 26.25 24H18C17.175 24 16.4688 23.7062 15.8813 23.1187C15.2938 22.5312 15 21.825 15 21V13.275L12.6 15.6L10.5 13.5L16.5 7.5L22.5 13.5L20.4 15.6L18 13.275V21H26.25C27.3 21 28.1875 20.6375 28.9125 19.9125C29.6375 19.1875 30 18.3 30 17.25C30 16.2 29.6375 15.3125 28.9125 14.5875C28.1875 13.8625 27.3 13.5 26.25 13.5H24V10.5C24 8.425 23.2687 6.65625 21.8062 5.19375C20.3438 3.73125 18.575 3 16.5 3C14.425 3 12.6562 3.73125 11.1938 5.19375C9.73125 6.65625 9 8.425 9 10.5H8.25C6.8 10.5 5.5625 11.0125 4.5375 12.0375C3.5125 13.0625 3 14.3 3 15.75C3 17.2 3.5125 18.4375 4.5375 19.4625C5.5625 20.4875 6.8 21 8.25 21H12V24H8.25Z"
      fill="#747783"
    />
  </svg>
);

const DELIVERY_TYPES = [
  { label: "Push", value: "PUSH", icon: <PushIcon /> },
  { label: "Email", value: "EMAIL", icon: <EmailIcon /> },
  { label: "SMS", value: "SMS", icon: <SmsIcon /> },
  { label: "In App", value: "IN_APP", icon: <InAppIcon /> },
];

const SUBSCRIPTION_PLANS = [
  { label: "All Plans", value: "ALL" },
  { label: "Simple Start", value: "SIMPLE_START" },
  { label: "Standard", value: "STANDARD" },
  { label: "Premium", value: "PREMIUM" },
  { label: "Enterprise", value: "ENTERPRISE" },
];

const RECIPIENTS = [
  { label: "Organization Users", value: "ORGANIZATION" },
  { label: "Employee Users", value: "EMPLOYEE" },
];

const PLATFORMS = [
  { label: "All Platforms", value: "ALL" },
  { label: "Main System", value: "MAIN" },
  { label: "Transmission Portal (FIRS)", value: "FIRS_EXTENSION" },
];

const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let min of [0, 30]) {
      const d = new Date();
      d.setHours(hour, min, 0, 0);
      slots.push({
        label: moment(d).format("hh:mm A"),
        value: d.toISOString(),
        dateObj: d,
      });
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

const EDITOR_TOOLBAR_CONFIG = {
  options: ["inline", "blockType", "fontSize", "textAlign", "list", "link", "history"],
  inline: { options: ["bold", "italic", "underline", "strikethrough"] },
  textAlign: {
    inDropdown: false,
    options: ["left", "center", "right", "justify"],
  },
};

const cleanGeneratedHtml = (rawHtml) => {
  if (!rawHtml) return "";
  return rawHtml
    .replace(/(<p>\s*&nbsp;\s*<\/p>){2,}/g, "<p>&nbsp;</p>")
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/g, "")
    .trim();
};

const getPlainTextLength = (html) => {
  if (!html) return 0;
  const plainText = html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return plainText.length;
};

const getWordCount = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).filter(Boolean).length;
};

const CreateNotifications = ({ isOpen, onClose, refetch }) => {
  const queryClient = useQueryClient();
  const imageRef = React.useRef();
  const [imageURL, setImageURL] = React.useState("");
  const [isPreview, setIsPreview] = React.useState(false);
  const [isMobileView, setIsMobileView] = React.useState(false);
  const [editorState, setEditorState] = React.useState(EditorState.createEmpty());

  const formHook = useForm({
    resolver: yupResolver(CREATE_NOTIFICATION_VALIDATION),
    mode: "all",
    defaultValues: defaultValues,
  });

  const errors = formHook.formState.errors;
  const control = formHook.control;
  const handleSubmit = formHook.handleSubmit;
  const reset = formHook.reset;
  const getValues = formHook.getValues;
  const setValue = formHook.setValue;
  const watch = formHook.watch;

  const deliveryTypes = useWatch({ control, name: "deliveryTypes", defaultValue: ["PUSH"] });
  const scheduleType = useWatch({ control, name: "scheduleType", defaultValue: "IMMEDIATE" });
  const messageValue = useWatch({ control, name: "message", defaultValue: "" });

  const currentPlainText = React.useMemo(() => {
    if (deliveryTypes?.includes("EMAIL")) {
      return editorState && typeof editorState.getCurrentContent === "function"
        ? editorState.getCurrentContent().getPlainText()
        : messageValue || "";
    }
    return messageValue || "";
  }, [deliveryTypes, editorState, messageValue]);

  const charCount = (currentPlainText || "").length;

  const wordCount = React.useMemo(() => {
    const trimmed = (currentPlainText || "").trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
  }, [currentPlainText]);

  const isEmailMode = deliveryTypes?.includes("EMAIL");
  const isOverLimit = isEmailMode ? wordCount > 200 : charCount > 150;
  const counterLabel = isEmailMode ? `${wordCount} / 200 words` : `${charCount} / 150 chars`;

  const mutation = useMutation({
    mutationFn: (data) => pushNotificationService.create(data),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      onCreateSuccess(data);
    },
  });

  const [orgSearchTerm, setOrgSearchTerm] = React.useState("");
  const [selectedOrgObjects, setSelectedOrgObjects] = React.useState([]);
  const debouncedOrgSearch = useDebounce(orgSearchTerm, 400);

  const isPushOnly = deliveryTypes?.includes("PUSH") && !deliveryTypes?.includes("EMAIL");

  const organizationsQuery = useQuery({
    queryKey: ["organizations-select-list", debouncedOrgSearch, isPushOnly],
    queryFn: async () => {
      if (isPushOnly) {
        // MODE A: Push Only -> fetch ONLY push-subscribed organizations
        try {
          const subRes = await pushNotificationService.getSubscriptions({ keyword: debouncedOrgSearch, limit: 100 });
          return subRes?.data?.data?.docs || subRes?.data?.data || subRes?.data?.docs || subRes?.data || [];
        } catch {
          return [];
        }
      } else {
        // MODE B: Email or Email + Push -> fetch ALL organizations
        try {
          const orgRes = await organizationService.get({ keyword: debouncedOrgSearch, limit: 50 });
          return orgRes?.data?.data?.docs || orgRes?.data?.data || orgRes?.data?.docs || orgRes?.data || [];
        } catch {
          return [];
        }
      }
    },
    enabled: !!isOpen,
  });

  const rawData = organizationsQuery?.data || [];

  // Extract organizations based on active mode
  const extractedOrgsMap = new Map();
  if (Array.isArray(rawData)) {
    rawData.forEach((item) => {
      if (isPushOnly) {
        // From push subscriptions endpoint
        const orgsArray = Array.isArray(item?.organizations)
          ? item.organizations
          : item?.organization
          ? [item.organization]
          : item?.org
          ? [item.org]
          : [];

        if (orgsArray.length > 0) {
          orgsArray.forEach((org) => {
            const orgId = typeof org === "object" ? org.id || org._id : org;
            const orgName = typeof org === "object" ? org.name || org.organizationName : org;
            if (orgId && !extractedOrgsMap.has(String(orgId))) {
              extractedOrgsMap.set(String(orgId), {
                label: orgName || orgId,
                value: String(orgId),
                status: item.isActive ? "ACTIVE" : "INACTIVE",
                platform: item.platform || "",
              });
            }
          });
        } else if (item?.organizationId) {
          const orgId = typeof item.organizationId === "object" ? item.organizationId._id || item.organizationId.id : item.organizationId;
          const orgName = typeof item.organizationId === "object" ? item.organizationId.name || item.organizationId.organizationName : orgId;
          if (orgId && !extractedOrgsMap.has(String(orgId))) {
            extractedOrgsMap.set(String(orgId), {
              label: orgName || orgId,
              value: String(orgId),
              status: item.isActive ? "ACTIVE" : "INACTIVE",
              platform: item.platform || "",
            });
          }
        }
      } else {
        // Direct organization documents
        const orgId = item._id || item.id;
        if (orgId && !extractedOrgsMap.has(String(orgId))) {
          extractedOrgsMap.set(String(orgId), {
            label: item.name || item.organizationName || item.email || orgId,
            value: String(orgId),
            status: item.subscription?.status || item.subscriptionStatus || (item.isActive ? "ACTIVE" : "INACTIVE"),
            plan: item.subscription?.planName || item.plan || "",
          });
        }
      }
    });
  }

  // Apply local search filtering so typing matches immediately
  const cleanKw = (debouncedOrgSearch || "").toLowerCase().trim();
  const filteredOrgs = Array.from(extractedOrgsMap.values()).filter((org) => {
    if (!cleanKw) return true;
    return String(org.label || "").toLowerCase().includes(cleanKw);
  });

  const ORGANIZATION_ITEMS = [
    { label: "All Organizations", value: "ALL" },
    ...filteredOrgs,
  ];

  // Handler that keeps track of selected org objects (label + value) so
  // pinned items stay visible in the dropdown even after clearing the search.
  const handleOrgChange = (e, field) => {
    const newValues = e.target.value;

    if (!Array.isArray(newValues) || newValues.includes("ALL")) {
      setSelectedOrgObjects([]);
      field.onChange(["ALL"]);
      return;
    }

    // Merge current search results with already-pinned items to resolve labels
    const allKnown = [
      ...selectedOrgObjects,
      ...ORGANIZATION_ITEMS.filter((i) => i.value !== "ALL"),
    ];
    const unique = allKnown.filter(
      (item, idx, arr) => arr.findIndex((i) => i.value === item.value) === idx
    );
    const nextPinned = unique.filter((item) => newValues.includes(item.value));

    setSelectedOrgObjects(nextPinned);
    field.onChange(newValues.length ? newValues : ["ALL"]);
  };

  const selectDeliveryType = (typeVal) => {
    const isEmail = typeVal === "EMAIL";
    const currentIsEmail = deliveryTypes?.includes("EMAIL");

    if (isEmail !== currentIsEmail) {
      setValue("message", "", { shouldValidate: true });
      setValue("text", "", { shouldValidate: true });
      setEditorState(EditorState.createEmpty());
    }

    setValue("deliveryTypes", [typeVal], { shouldValidate: true, shouldDirty: true });
  };

  const { isPending } = mutation;

  const customInputStyle = {
    backgroundColor: "#EFF4FF",
    borderRadius: "8px",
    fontSize: "14px",
    height: "54px",
    color: "#0B1C30",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: 500,
    color: "#434651",
    letterSpacing: "0.05em",
    marginBottom: "6px",
    display: "block",
  };

  const handleEditorChange = React.useCallback(
    (newEditorState) => {
      onEditorStateChange(newEditorState);
      const rawContent = convertToRaw(newEditorState.getCurrentContent());
      const rawHtml = draftToHtml(rawContent);
      const cleanedHtml = cleanGeneratedHtml(rawHtml);
      setValue("message", cleanedHtml, { shouldValidate: true, shouldDirty: true });
      setValue("text", cleanedHtml, { shouldValidate: true, shouldDirty: true });
    },
    [setValue]
  );

  return (
    <React.Fragment>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "16px",
            padding: "32px",
            maxWidth: "765px",
            width: "100%",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          },
          component: "form",
          onSubmit: handleSubmit(save),
        }}
      >
        {/* Modal Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="fw-semibold mb-0" style={{ fontSize: "24px", color: "#0B1C30" }}>
            Create New Message
          </h3>
          <IconButton onClick={onClose} size="small" style={{ color: "#747783" }}>
            <Close />
          </IconButton>
        </div>

        <DialogContent className="p-0 overflow-auto" style={{ maxHeight: "75vh" }}>
          <PreviewNotification
            isPreview={isPreview}
            isMobileView={isMobileView}
            setIsMobileView={setIsMobileView}
            data={{
              title: getValues("title"),
              message: getValues("message") || getValues("text"),
              image: imageURL,
            }}
          />
          <div className={isPreview ? "d-none" : "d-block"}>
            {/* MESSAGE TITLE */}
            <div className="mb-4">
              <label style={labelStyle}>
                MESSAGE TITLE <span className="text-danger">*</span>
              </label>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="e.g. Weekly Product Update"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors["title"]?.message}
                    error={!!errors["title"]}
                    InputProps={{ style: customInputStyle }}
                  />
                )}
              />
            </div>

            {/* ROW 1: RECIPIENTS GROUP & SUBSCRIPTION PLANS */}
            <div className="row g-3 mb-4">
              <div className="col-12 col-md-6">
                <label style={labelStyle}>
                  RECIPIENTS GROUP <span className="text-danger">*</span>
                </label>
                <Controller
                  name="recipients"
                  control={control}
                  render={({ field }) => (
                    <MultipleSelectMenu
                      placeholder={"All Registered Users"}
                      value={field.value}
                      error={!!errors["recipients"]}
                      onChange={(e) => field.onChange(e.target.value)}
                      items={RECIPIENTS}
                      helperText={errors["recipients"]?.message}
                      customInputStyle={customInputStyle}
                    />
                  )}
                />
              </div>

              <div className="col-12 col-md-6">
                <label style={labelStyle}>
                  SUBSCRIPTION PLANS <span className="text-danger">*</span>
                </label>
                <Controller
                  name="subscriptionPlans"
                  control={control}
                  render={({ field }) => (
                    <MultipleSelectMenu
                      placeholder={"All Plans"}
                      value={field.value}
                      error={!!errors["subscriptionPlans"]}
                      onChange={(e) => field.onChange(e.target.value)}
                      items={SUBSCRIPTION_PLANS}
                      helperText={errors["subscriptionPlans"]?.message}
                      customInputStyle={customInputStyle}
                    />
                  )}
                />
              </div>
            </div>

            {/* TARGET PLATFORMS */}
            <div className="mb-4">
              <label style={labelStyle}>TARGET PLATFORMS (OPTIONAL)</label>
              <Controller
                name="platforms"
                control={control}
                render={({ field }) => (
                  <MultipleSelectMenu
                    placeholder={"All Platforms"}
                    value={field.value || ["ALL"]}
                    error={!!errors["platforms"]}
                    onChange={(e) => field.onChange(e.target.value)}
                    items={PLATFORMS}
                    helperText={errors["platforms"]?.message}
                    customInputStyle={customInputStyle}
                  />
                )}
              />
            </div>

            {/* TARGET ORGANIZATIONS */}
            <div className="mb-4">
              <label style={labelStyle}>TARGET ORGANIZATIONS (OPTIONAL)</label>
              <Controller
                name="organizationIds"
                control={control}
                render={({ field }) => (
                  <MultipleSelectMenu
                    placeholder={"All Organizations"}
                    value={field.value || ["ALL"]}
                    error={!!errors["organizationIds"]}
                    onChange={(e) => handleOrgChange(e, field)}
                    items={ORGANIZATION_ITEMS}
                    pinnedItems={selectedOrgObjects}
                    helperText={errors["organizationIds"]?.message}
                    customInputStyle={customInputStyle}
                    searchable
                    searchValue={orgSearchTerm}
                    onSearchChange={(val) => setOrgSearchTerm(val)}
                    isSearchLoading={organizationsQuery.isFetching}
                  />
                )}
              />
            </div>

            {/* ROW 2: DELIVERY CHANNELS */}
            <div className="mb-4">
              <label style={labelStyle}>
                DELIVERY CHANNELS <span className="text-danger">*</span>
              </label>
              <div className="row g-3">
                {DELIVERY_TYPES.map((type) => {
                  const isChecked = deliveryTypes?.includes(type.value);
                  return (
                    <div className="col-6 col-md-3" key={type.value}>
                      <div
                        className="d-flex align-items-center gap-2 px-3 border rounded cursor-pointer transition-all"
                        style={{
                          height: "54px",
                          backgroundColor: isChecked ? "#EFF4FF" : "#FFFFFF",
                          borderColor: isChecked ? "#0075FF" : "#C3C6D3",
                          borderRadius: "8px",
                          userSelect: "none",
                        }}
                        onClick={() => selectDeliveryType(type.value)}
                      >
                        <Radio
                          size="small"
                          checked={isChecked}
                          style={{ padding: 0, color: isChecked ? "#0075FF" : "#747783" }}
                        />
                        <span style={{ fontSize: "15px" }}>{type.icon}</span>
                        <span className="fw-medium" style={{ fontSize: "14px", color: "#0B1C30" }}>
                          {type.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {errors["deliveryTypes"] && (
                <div className="text-danger fs-xs mt-1">{errors["deliveryTypes"]?.message}</div>
              )}
            </div>

            {/* ROW 3: LAUNCH SCHEDULED */}
            <div className="mb-4">
              <label style={labelStyle}>
                LAUNCH SCHEDULED <span className="text-danger">*</span>
              </label>

              <Controller
                name="scheduleType"
                control={control}
                render={({ field }) => (
                  <Select
                    fullWidth
                    size="small"
                    value={field.value || "IMMEDIATE"}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      if (e.target.value === "IMMEDIATE") {
                        setValue("date", new Date());
                        setValue("time", new Date());
                      }
                    }}
                    style={customInputStyle}
                  >
                    <MenuItem value="IMMEDIATE">Immediate</MenuItem>
                    <MenuItem value="SCHEDULED">Schedule for Future Date & Time</MenuItem>
                  </Select>
                )}
              />

              {/* Show Modern Icon-Adorned Date & Time Pickers when SCHEDULED is selected */}
              {scheduleType === "SCHEDULED" && (
                <div className="row g-3 mt-1">
                  <div className="col-12 col-md-6">
                    <Controller
                      name="date"
                      control={control}
                      render={({ field }) => (
                        <FormControl error={!!errors.date} fullWidth>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DesktopDatePicker
                              inputFormat="EEE, MMM dd, yyyy"
                              value={field.value}
                              onChange={(e) => field.onChange(e)}
                              minDate={new Date()}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  size="small"
                                  {...params}
                                  InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                      <CalendarToday style={{ color: "#1F53D7", fontSize: "18px", marginRight: "8px" }} />
                                    ),
                                    style: customInputStyle,
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        </FormControl>
                      )}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <Controller
                      name="time"
                      control={control}
                      render={({ field }) => (
                        <FormControl error={!!errors.time} fullWidth>
                          <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={
                              field.value
                                ? TIME_SLOTS.find((s) => moment(s.dateObj).format("HH:mm") === moment(field.value).format("HH:mm"))?.value || ""
                                : ""
                            }
                            onChange={(e) => {
                              const selectedSlot = TIME_SLOTS.find((s) => s.value === e.target.value);
                              field.onChange(selectedSlot ? selectedSlot.dateObj : null);
                            }}
                            startAdornment={
                              <AccessTime style={{ color: "#1F53D7", fontSize: "18px", marginLeft: "12px", marginRight: "4px" }} />
                            }
                            style={customInputStyle}
                          >
                            <MenuItem disabled value="">
                              Select Time Slot
                            </MenuItem>
                            {TIME_SLOTS.map((slot, idx) => (
                              <MenuItem value={slot.value} key={idx}>
                                {slot.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* UNIFIED MESSAGE BODY CONTENT WITH INDUSTRY STANDARD COUNTER */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label style={{ ...labelStyle, marginBottom: 0 }}>
                  MESSAGE BODY <span className="text-danger">*</span>
                </label>
                <span
                  className={`fs-xs fw-semibold px-2 py-1 rounded-pill border ${
                    isOverLimit ? "bg-danger text-white border-danger" : "bg-white text-secondary border-light-subtle"
                  }`}
                  style={{ fontSize: "11px", letterSpacing: "0.03em" }}
                >
                  {counterLabel}
                </span>
              </div>
              {deliveryTypes?.includes("EMAIL") ? (
                <div
                  className="border rounded-2 p-2"
                  style={{
                    backgroundColor: "#EFF4FF",
                    borderColor: isOverLimit ? "#dc3545" : "#C3C6D3",
                    borderRadius: "8px",
                    minHeight: "180px",
                  }}
                >
                  <style>{`
                    .demo-editor {
                      line-height: 1.75 !important;
                      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif !important;
                      color: #0B1C30;
                      font-size: 14px;
                      width: 100% !important;
                      max-width: 100% !important;
                    }
                    .demo-editor p,
                    .demo-editor div[data-block="true"] {
                      margin-bottom: 10px !important;
                      margin-top: 0 !important;
                      padding: 0 !important;
                      width: 100% !important;
                      max-width: 100% !important;
                    }
                    .demo-editor .rdw-text-align-left,
                    .demo-editor div[style*="text-align:left"],
                    .demo-editor div[style*="text-align: left"],
                    .demo-editor div[data-block="true"][style*="text-align: left"] {
                      text-align: left !important;
                    }
                    .demo-editor .rdw-text-align-center,
                    .demo-editor div[style*="text-align:center"],
                    .demo-editor div[style*="text-align: center"],
                    .demo-editor div[data-block="true"][style*="text-align: center"] {
                      text-align: center !important;
                    }
                    .demo-editor .rdw-text-align-right,
                    .demo-editor div[style*="text-align:right"],
                    .demo-editor div[style*="text-align: right"],
                    .demo-editor div[data-block="true"][style*="text-align: right"] {
                      text-align: right !important;
                    }
                    .demo-editor .rdw-text-align-justify,
                    .demo-editor div[style*="text-align:justify"],
                    .demo-editor div[style*="text-align: justify"],
                    .demo-editor div[data-block="true"][style*="text-align: justify"],
                    .demo-editor div[data-block="true"][style*="text-align:justify"],
                    .demo-editor .public-DraftStyleDefault-justify {
                      text-align: justify !important;
                      text-align-last: left !important;
                      text-justify: inter-word !important;
                      hyphens: auto !important;
                      -webkit-hyphens: auto !important;
                      -ms-hyphens: auto !important;
                      width: 100% !important;
                      display: block !important;
                    }
                    .demo-editor .public-DraftEditor-content {
                      width: 100% !important;
                    }
                    .demo-editor .public-DraftEditor-content [data-block="true"] {
                      width: 100% !important;
                      max-width: 100% !important;
                    }
                  `}</style>
                  <Editor
                    editorState={editorState}
                    stripPastedStyles={true}
                    toolbarClassName="border-bottom pb-2 mb-2"
                    wrapperClassName="demo-wrapper"
                    editorClassName="demo-editor bg-white p-3 rounded border"
                    editorStyle={{
                      minHeight: "150px",
                      color: "#0B1C30",
                      fontSize: "14px",
                      lineHeight: "1.75",
                      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
                    }}
                    onEditorStateChange={handleEditorChange}
                    toolbar={EDITOR_TOOLBAR_CONFIG}
                  />
                </div>
              ) : (
                <Controller
                  name="message"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      variant="outlined"
                      placeholder="Enter message text here..."
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setValue("message", e.target.value, { shouldValidate: true, shouldDirty: true });
                        setValue("text", e.target.value, { shouldValidate: true, shouldDirty: true });
                      }}
                      helperText={errors["message"]?.message}
                      error={!!errors["message"] || isOverLimit}
                      InputProps={{
                        style: {
                          backgroundColor: "#EFF4FF",
                          border: isOverLimit ? "1px solid #dc3545" : "1px solid #C3C6D3",
                          borderRadius: "8px",
                          fontSize: "14px",
                          color: "#0B1C30",
                          padding: "12px",
                        },
                      }}
                    />
                  )}
                />
              )}
              {isOverLimit && (
                <div className="text-danger fs-xs mt-1">
                  Message exceeds the maximum limit ({isEmailMode ? "200 words" : "150 characters"}).
                </div>
              )}
            </div>

            {/* BANNER IMAGE DROPZONE */}
            <div className="mb-4">
              <label style={labelStyle}>BANNER IMAGE</label>
              <div
                className="border rounded-3 p-4 text-center cursor-pointer transition-all"
                style={{
                  borderStyle: "dashed",
                  borderWidth: "2px",
                  borderColor: "#C3C6D3",
                  backgroundColor: "rgba(239, 244, 255, 0.30)",
                  backgroundImage: imageURL ? `url(${imageURL})` : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  borderRadius: "12px",
                }}
                onClick={() => imageRef.current?.click()}
              >
                {!imageURL ? (
                  <>
                    <div className="mb-2">
                      <CloudUploadFigmaIcon />
                    </div>
                    <div className="fw-medium mb-1" style={{ fontSize: "14px", color: "#00347E" }}>
                      Drag and drop or browse files
                    </div>
                    <div className="fs-xs mb-3" style={{ fontSize: "12px", color: "#747783", letterSpacing: "0.05em" }}>
                      PNG, JPG up to 5MB (1200x600 recommended)
                    </div>
                    <Button
                      variant="contained"
                      disableElevation
                      style={{
                        backgroundColor: "#D3E4FE",
                        color: "#434651",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "12px",
                        padding: "8px 24px",
                      }}
                    >
                      Choose File
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      imageRef.current?.click();
                    }}
                  >
                    Change Banner Image
                  </Button>
                )}
              </div>
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <input
                    className="d-none"
                    ref={imageRef}
                    type="file"
                    name="image"
                    id="image"
                    accept=".jpg, .png, .jpeg, .gif"
                    onChange={(e) => {
                      const image = e.target.files[0];
                      if (image) {
                        const imageURL = URL.createObjectURL(image);
                        field.onChange(image);
                        setImageURL(imageURL);
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>
        </DialogContent>

        {/* MODAL FOOTER BUTTONS */}
        <div className="row g-3 pt-3">
          <div className="col-6">
            {isPreview ? (
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={() => setIsPreview(false)}
                style={{
                  borderRadius: "8px",
                  borderColor: "#1F53D7",
                  color: "#1F53D7",
                  height: "56px",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Edit Message
              </Button>
            ) : (
              <Button
                type="button"
                fullWidth
                variant="outlined"
                onClick={() => setIsPreview(true)}
                style={{
                  borderRadius: "8px",
                  borderColor: "#1F53D7",
                  color: "#1F53D7",
                  height: "56px",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                See Preview
              </Button>
            )}
          </div>
          <div className="col-6">
            <Button
              type="submit"
              fullWidth
              disabled={isPending}
              startIcon={<BTNLoader isLoading={isPending} />}
              variant="contained"
              style={{
                borderRadius: "8px",
                backgroundColor: "#1F53D7",
                color: "#FFF",
                height: "56px",
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 10px 15px -3px rgba(0, 52, 126, 0.30)",
              }}
            >
              Send Message
            </Button>
          </div>
        </div>
      </Dialog>
    </React.Fragment>
  );

  async function save(data) {
    const isEmail = deliveryTypes?.includes("EMAIL");
    const rawMessage = data?.message || "";

    if (!rawMessage.trim()) {
      return toast.error("Message content is required.");
    }

    // Filter recipients to guarantee valid backend enum values
    let recipientsPayload = (data.recipients || [])
      .map((r) => (r === "ALL" ? ["ORGANIZATION", "EMPLOYEE"] : r))
      .flat()
      .filter((r) => ["ORGANIZATION", "EMPLOYEE"].includes(r));

    if (!recipientsPayload.length) {
      recipientsPayload = ["ORGANIZATION", "EMPLOYEE"];
    }

    // Filter subscriptionPlans to guarantee valid backend enum values
    const allPlans = ["SIMPLE_START", "STANDARD", "PREMIUM", "ENTERPRISE"];
    let plansPayload = (data.subscriptionPlans || [])
      .map((p) => (p === "ALL" ? allPlans : p))
      .flat()
      .filter((p) => allPlans.includes(p));

    if (!plansPayload.length) {
      plansPayload = allPlans;
    }

    // Filter platforms to narrow push delivery if specified
    let platformsPayload = (data.platforms || [])
      .filter((p) => ["MAIN", "FIRS_EXTENSION"].includes(p));

    // Filter organizationIds to omit ALL
    let orgsPayload = (data.organizationIds || []).filter((id) => id !== "ALL");

    let plainText = "";
    let htmlText = "";

    if (isEmail) {
      const contentState = editorState?.getCurrentContent ? editorState.getCurrentContent() : null;
      htmlText = contentState ? draftToHtml(convertToRaw(contentState)) : rawMessage;
      plainText = contentState ? contentState.getPlainText() : rawMessage.replace(/<[^>]*>/g, "");
    } else {
      plainText = rawMessage;
      htmlText = rawMessage;
    }

    mutation.mutate({
      ...data,
      message: plainText?.trim() ? plainText : rawMessage,
      text: htmlText?.trim() ? htmlText : rawMessage,
      recipients: recipientsPayload,
      subscriptionPlans: plansPayload,
      ...(platformsPayload.length ? { platforms: platformsPayload } : {}),
      ...(orgsPayload.length ? { organizationIds: orgsPayload } : {}),
    });
  }

  function onEditorStateChange(newEditorState) {
    setEditorState(newEditorState);
    const contentState = newEditorState.getCurrentContent();
    const result = draftToHtml(convertToRaw(contentState));
    setValue("message", result, { shouldValidate: true, shouldDirty: true });
    setValue("text", result, { shouldValidate: true, shouldDirty: true });
  }

  function onCreateSuccess(data) {
    const date = moment(data?.data?.dateTime).format("Do MMM, YYYY");
    const time = moment(data?.data?.dateTime).format("LT");
    toast.success(`Scheduled notification for ${date} at ${time} has been successfully queued.`);
    queryClient.invalidateQueries({ queryKey: ["push-notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-stats"] });
    if (typeof refetch === "function") {
      refetch();
    }
    reset(defaultValues);
    setImageURL("");
    setIsPreview(false);
    onClose();
  }
};

function MultipleSelectMenu(props) {
  const {
    value = [],
    onChange,
    items,
    pinnedItems = [],
    error,
    helperText,
    placeholder,
    customInputStyle,
    searchable = false,
    searchValue = "",
    onSearchChange,
    isSearchLoading = false,
  } = props;

  // All known labels: pinned items + current search items (deduplicated)
  const allKnownItems = [
    ...pinnedItems,
    ...(items || []).filter(
      (i) => !pinnedItems.some((p) => p.value === i.value)
    ),
  ];

  const handleSelectChange = (event) => {
    const selected = typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;
    const hasAllOption = items?.some((i) => i.value === "ALL");

    if (hasAllOption) {
      const wasAllSelected = value.includes("ALL");
      const isAllSelectedNow = selected.includes("ALL");

      if (!wasAllSelected && isAllSelectedNow) {
        return onChange({ target: { value: ["ALL"] } });
      }
      if (wasAllSelected && selected.length > 1 && isAllSelectedNow) {
        const withoutAll = selected.filter((v) => v !== "ALL");
        return onChange({ target: { value: withoutAll } });
      }
      if (!selected.length) {
        return onChange({ target: { value: ["ALL"] } });
      }
    }

    onChange(event);
  };

  // Resolve display label from pinned items first (so names survive search clear)
  const resolveLabel = (val) => {
    const fromPinned = pinnedItems.find((i) => i.value === val);
    if (fromPinned) return fromPinned.label;
    const fromItems = items?.find((i) => i.value === val);
    return fromItems ? fromItems.label : val.replace("_", " ");
  };

  // Search result items excluding already-pinned ones (no duplicates in list)
  const searchResultItems = (items || []).filter(
    (i) => i.value === "ALL" || !pinnedItems.some((p) => p.value === i.value)
  );

  return (
    <FormControl error={error} fullWidth>
      <Select
        multiple
        fullWidth
        size="small"
        displayEmpty
        value={value}
        onChange={handleSelectChange}
        renderValue={(selected) =>
          !selected.length || (selected.length === 1 && selected[0] === "ALL")
            ? placeholder
            : selected.filter((v) => v !== "ALL").map(resolveLabel).join(", ")
        }
        style={customInputStyle}
        MenuProps={{ autoFocus: false }}
      >
        {/* Sticky search input */}
        {searchable && (
          <div
            onKeyDown={(e) => e.stopPropagation()}
            style={{ padding: "8px 12px", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}
          >
            <TextField
              autoFocus
              fullWidth
              size="small"
              placeholder="Search organizations..."
              value={searchValue}
              onChange={(e) => onSearchChange?.(e.target.value)}
              InputProps={{
                endAdornment: isSearchLoading ? (
                  <span style={{ fontSize: 12, color: "#999", paddingRight: 4 }}>Loading...</span>
                ) : null,
              }}
            />
          </div>
        )}

        {/* Pinned selected orgs — always visible at top */}
        {searchable && pinnedItems.length > 0 && (
          <MenuItem disabled style={{ opacity: 1, paddingBottom: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#0075FF", textTransform: "uppercase", letterSpacing: "0.5px" }}>
              Selected ({pinnedItems.length})
            </span>
          </MenuItem>
        )}
        {searchable && pinnedItems.map((item, index) => (
          <MenuItem value={item.value} key={`pinned-${index}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1, marginRight: 8 }}>
              <Checkbox size="small" checked={value.indexOf(item.value) > -1} style={{ color: "#0075FF" }} />
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ style: { fontWeight: 500, color: "#0B1C30", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}
              />
            </div>
            {item.status && <StatusPill status={item.status} />}
          </MenuItem>
        ))}
        {searchable && pinnedItems.length > 0 && (
          <MenuItem disabled style={{ padding: "4px 0" }}>
            <div style={{ width: "100%", borderTop: "1px solid #E5E7EB", margin: "0 12px" }} />
          </MenuItem>
        )}

        {/* Placeholder item */}
        <MenuItem disabled value="">{placeholder}</MenuItem>

        {/* Empty state hints */}
        {searchable && !searchValue && pinnedItems.length === 0 && (
          <MenuItem disabled>
            <span style={{ fontSize: 13, color: "#999" }}>Type to search organizations...</span>
          </MenuItem>
        )}
        {searchable && searchValue && searchResultItems.length <= 1 && !isSearchLoading && (
          <MenuItem disabled>
            <span style={{ fontSize: 13, color: "#999" }}>No organizations found</span>
          </MenuItem>
        )}

        {/* Search results (excluding pinned) */}
        {searchResultItems.map((item, index) => (
          <MenuItem value={item?.value} key={`result-${index}`} disabled={item?.disabled} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1, marginRight: 8 }}>
              <Checkbox size="small" checked={value.indexOf(item?.value) > -1} />
              <ListItemText primary={item?.label} primaryTypographyProps={{ style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }} />
            </div>
            {item?.status && <StatusPill status={item.status} />}
          </MenuItem>
        ))}
      </Select>

      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
}

const StatusPill = ({ status }) => {
  const normalizedStatus = String(status || "INACTIVE").toUpperCase();

  if (normalizedStatus === "ACTIVE") {
    return (
      <span
        style={{
          fontSize: "11px",
          fontWeight: 600,
          padding: "3px 10px",
          borderRadius: "12px",
          backgroundColor: "#E6F4EA",
          color: "#137333",
          border: "1px solid #CEEAD6",
          whiteSpace: "nowrap",
          display: "inline-block",
          lineHeight: "1.2",
        }}
      >
        Active
      </span>
    );
  }

  if (normalizedStatus === "EXPIRED") {
    return (
      <span
        style={{
          fontSize: "11px",
          fontWeight: 600,
          padding: "3px 10px",
          borderRadius: "12px",
          backgroundColor: "#FCE8E6",
          color: "#C5221F",
          border: "1px solid #FAD2CF",
          whiteSpace: "nowrap",
          display: "inline-block",
          lineHeight: "1.2",
        }}
      >
        Expired
      </span>
    );
  }

  return (
    <span
      style={{
        fontSize: "11px",
        fontWeight: 600,
        padding: "3px 10px",
        borderRadius: "12px",
        backgroundColor: "#FEF7E0",
        color: "#B06000",
        border: "1px solid #FCE8B2",
        whiteSpace: "nowrap",
        display: "inline-block",
        lineHeight: "1.2",
      }}
    >
      Inactive
    </span>
  );
};

export default CreateNotifications;
